import {
  DataGridPremiumProps,
  GridColDef,
  GridColumnGroupingModel,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridValidRowModel,
  GridEventListener,
  GridCallbackDetails,
} from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import {
  DictionarySetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";

type WithByColName = Record<string, number>;

export type UseColumnsInputType<R extends GridValidRowModel> = {
  tableId: string;
  columns: Array<GridColDef<R>>;
  pinnedColumns?: GridPinnedColumnFields;
  defaultColumnVisibility?: GridColumnVisibilityModel;
  columnGroupingModel?: GridColumnGroupingModel;
};

export type UseColumnsReturnType<R extends GridValidRowModel> = {
  columns: Array<GridColDef<R>>;
  pinnedColumns: GridPinnedColumnFields | undefined;
  columnVisibility?: GridColumnVisibilityModel | undefined;
  columnGroupingModel: GridColumnGroupingModel | undefined;
  handleColumnVisibilityChange: DataGridPremiumProps["onColumnVisibilityModelChange"];
  handleColumnWidthChange: DataGridPremiumProps["onColumnWidthChange"];
  resetColumnVisibility: () => void;
  resetColumnWidths: () => void;
};

export const useColumns = <R extends GridValidRowModel>(
  input: UseColumnsInputType<R>
): UseColumnsReturnType<R> => {
  const [columnVisibility, setColumnVisibility] =
    useUserSettingWithDefault<GridColumnVisibilityModel>(
      `${input.tableId}.columnVisibilityModel`,
      DictionarySetting,
      input.defaultColumnVisibility ?? {},
      {
        storeLocally: true,
      }
    );

  const [widths, setWidths] = useUserSettingWithDefault<WithByColName>(
    `${input.tableId}.columnWidths`,
    DictionarySetting,
    {},
    { storeLocally: true }
  );

  const columns = useMemo<GridColDef[]>(
    () =>
      input.columns.map((c) => ({
        ...c,
        width: widths[c.field] ?? c.width, // apply stored width if any
      })),
    [input.columns, widths]
  );

  const handleColumnVisibilityChange = useCallback<
    (model: GridColumnVisibilityModel, details: GridCallbackDetails) => void
  >(
    (model) => {
      setColumnVisibility(model);
    },
    [setColumnVisibility]
  );

  const handleColumnWidthChange = useCallback<
    GridEventListener<"columnWidthChange">
  >(
    (params, event, details) => {
      setWidths((prevWidths) => ({
        ...prevWidths,
        [params.colDef.field]: params.width,
      }));
    },
    [setWidths]
  );

  const handleResetColumnVisibility = useCallback(() => {
    setColumnVisibility(input.defaultColumnVisibility ?? {});
  }, [input.defaultColumnVisibility, setColumnVisibility]);

  const handleResetColumnWidths = useCallback(() => {
    setWidths({});
  }, [setWidths]);

  return {
    columns: columns,
    pinnedColumns: input.pinnedColumns,
    columnVisibility: columnVisibility,
    columnGroupingModel: input.columnGroupingModel,
    handleColumnVisibilityChange: handleColumnVisibilityChange,
    handleColumnWidthChange: handleColumnWidthChange,
    resetColumnVisibility: handleResetColumnVisibility,
    resetColumnWidths: handleResetColumnWidths,
  };
};
